/**
 * Scene slice — project entity state.
 */
import type { StateCreator } from 'zustand';
import type { StoreState } from '../useStore';
import type { GeometryBounds } from '@/components/mesh-viewer/renderer';
import type { DiagnosisSummary } from '@/stores/schemas';
import { TessellatedMesh } from '@/workers/stepTessellator.worker';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Stripped diagnosis result for agent context — no coordinate arrays. Derived
// from `diagnosisSummarySchema` in `@/stores/schemas` (single source of truth);
// re-exported here so existing `DiagnosisSummary` consumers keep resolving. The
// two-line `import type` + `export type {}` form keeps the name bound locally
// (used below in this slice) AND re-exported for external consumers — a bare
// `export type { … } from` would not bind it locally.
export type { DiagnosisSummary };

// ---------------------------------------------------------------------------
// Slice interface
// ---------------------------------------------------------------------------

export interface SceneSlice {
  // Project identity
  currentProject: string | null; // geometry_id
  s3Key: string | null;

  // Selected mesh run (null = latest mesh)
  selectedMeshId: string | null;
  selectedMeshCompleted: boolean;
  previewSelected: boolean;

  // Active geometry version in the tree (null = fallback to the displayed lineage
  // tip via `lineageTipId`, not raw lineage.current_geometry_id)
  activeGeometryId: string | null;

  // Logs
  logs: string[];

  // Client-side tessellation (MeshViewer)
  cadPreviewGeometries: TessellatedMesh[] | null;
  isClientTessellating: boolean;
  // Why the last client tessellation failed (null = no failure). Singular
  // MeshGen flow only — set by useClientTessellation, cleared when a new
  // tessellation starts. Drives the viewer's preview-unavailable state.
  cadPreviewError: string | null;

  // Per-cell-type counts pushed by MeshViewer when its renderer hands back a
  // ready handle. Set on mesh load, cleared on viewer reset/unmount or when
  // the active mesh changes. null when no viewer is mounted or the renderer
  // hasn't finished parsing — consumers (MeshResultsPanel) fall back to the
  // backend summary in that case.
  viewerCellCounts: {
    tris: number;
    quads: number;
    tets: number;
    hexs: number;
    psms: number;
    pyrs: number;
    polys: number;
  } | null;

  // Per-(entity-tag, dim) cell counts mirrored from the renderer when it
  // finishes parsing a `.msh`. Used by the CHT multi-region viewer to
  // list the regions the user can toggle on/off — Bug 2 from the BE
  // diagnosis. null when no viewer is mounted or the parse didn't yield
  // region info (CAD preview path).
  viewerRegions: Array<{ tag: number; dim: number; count: number }> | null;

  // Which volume-region entity tags the user has marked visible. null is
  // the implicit "all visible" default (set when the renderer first
  // mounts or after a mesh change). Toggling a checkbox in
  // `RegionInfoChip` replaces this with an explicit Set; clearing
  // all checkboxes leaves an empty Set (everything hidden). The set
  // contains gmsh entity tags as integers, matching the
  // RegionInfoChip / RendererHandle.setVisibleRegions contract.
  regionVisibility: Set<number> | null;

  // CAD bounding-box diagonal (mm) — computed from client tessellation
  cadDiagonal: number | null;

  // Per-axis bounding-box extents + detected STEP unit, for domain factor UI
  cadBounds: { lx: number; ly: number; lz: number; unit: string | null } | null;

  // Bounds of the geometry actually mounted in MeshViewer's renderer (pushed
  // via reportGeometryBounds mixin once parsing finishes). Distinct from
  // cadBounds — that's CAD-tessellation derived and exists pre-renderer;
  // this one tracks what the renderer is actually displaying. Used by the
  // chat sidebar to give the agent spatial context.
  geometryBounds: GeometryBounds | null;

  // Per-geometry tessellation cache. SimOps tracks multiple geometries at
  // once and looks them up by id; meshgen still uses the singular fields
  // above for its single-project flow.
  cadPreviewByGeometryId: Record<
    string,
    { meshes: TessellatedMesh[]; diagonal: number }
  >;

  diagnosisSummary: DiagnosisSummary | null;

  // Actions
  setCurrentProject: (v: string | null) => void;
  setS3Key: (v: string | null) => void;

  setLogs: (v: string[] | ((prev: string[]) => string[])) => void;
  addLog: (v: string) => void;
  appendLogs: (v: string[]) => void;

  setCadPreviewGeometries: (v: TessellatedMesh[] | null) => void;
  setIsClientTessellating: (v: boolean) => void;
  setCadPreviewError: (v: string | null) => void;
  setViewerCellCounts: (v: SceneSlice['viewerCellCounts']) => void;
  setViewerRegions: (v: SceneSlice['viewerRegions']) => void;
  setRegionVisibility: (v: SceneSlice['regionVisibility']) => void;
  /**
   * Flip a single tag in `regionVisibility`. Lazily promotes the null
   * "all visible" default to a Set covering every currently-known volume
   * tag, then toggles `tag` so the very first checkbox click does the
   * expected thing instead of clearing everything.
   */
  toggleRegionVisibility: (tag: number, allKnownTags: number[]) => void;
  setCadDiagonal: (v: number | null) => void;
  setCadBounds: (
    v: { lx: number; ly: number; lz: number; unit: string | null } | null,
  ) => void;
  setGeometryBounds: (v: GeometryBounds | null) => void;
  setCadPreviewForGeometry: (
    geometryId: string,
    data: { meshes: TessellatedMesh[]; diagonal: number } | null,
  ) => void;

  setSelectedMesh: (id: string | null, completed: boolean) => void;
  setPreviewSelected: (v: boolean) => void;
  setActiveGeometryId: (v: string | null) => void;

  /** Show the CAD preview for a geometry version (active geometry + preview mode + clear mesh selection). */
  showGeometryPreview: (geometryId: string) => void;
  /** Select a mesh in the tree (clears preview mode, treats mesh as completed). */
  selectMeshInTree: (meshId: string) => void;

  setDiagnosisSummary: (v: DiagnosisSummary | null) => void;

  resetProjectState: () => void;
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const sceneInitialState: Omit<
  SceneSlice,
  | `set${string}`
  | 'addLog'
  | 'appendLogs'
  | 'resetProjectState'
  | 'showGeometryPreview'
  | 'selectMeshInTree'
  | 'toggleRegionVisibility'
> = {
  currentProject: null,
  s3Key: null,
  selectedMeshId: null,
  selectedMeshCompleted: false,
  previewSelected: false,
  activeGeometryId: null,
  logs: [],
  cadPreviewGeometries: null,
  viewerCellCounts: null,
  viewerRegions: null,
  regionVisibility: null,
  isClientTessellating: false,
  cadPreviewError: null,
  cadDiagonal: null,
  cadBounds: null,
  geometryBounds: null,
  cadPreviewByGeometryId: {},
  diagnosisSummary: null,
};

/**
 * What a change of active version resets (see `setActiveGeometryId` for why).
 * Every writer of `activeGeometryId` spreads this, so a new version-scoped
 * field is added in one place, not in each writer.
 */
function versionScopedReset(
  from: string | null,
  to: string | null,
): Partial<StoreState> | null {
  return from === to
    ? null
    : { faceTagAssignments: [], wallGroups: [], blPatchNames: undefined };
}

// ---------------------------------------------------------------------------
// Slice creator
// ---------------------------------------------------------------------------

export const createSceneSlice: StateCreator<
  StoreState,
  [['zustand/devtools', never], ['zustand/subscribeWithSelector', never]],
  [],
  SceneSlice
> = (set) => ({
  ...sceneInitialState,

  // A different project starts with no mesh selected and no version pinned,
  // cleared in the same write that switches it. `useInitialProjectLoad`
  // switches before `MeshGenWorkspace` hydrates `?mesh=` / `?geometry=`, so a
  // link's selection wins; a clear that ran later would wipe it (ENG-2687).
  setCurrentProject: (v) =>
    set((s) =>
      s.currentProject === v
        ? s
        : {
            currentProject: v,
            selectedMeshId: null,
            selectedMeshCompleted: false,
            previewSelected: false,
            activeGeometryId: null,
            ...versionScopedReset(s.activeGeometryId, null),
          },
    ),
  setS3Key: (v) => set({ s3Key: v }),
  setSelectedMesh: (id, completed) =>
    set({
      selectedMeshId: id,
      selectedMeshCompleted: completed,
      previewSelected: false,
    }),
  setPreviewSelected: (v) =>
    set({
      previewSelected: v,
      selectedMeshId: null,
      selectedMeshCompleted: false,
    }),
  // Switching geometry invalidates every face pick made against the old one:
  // `faceTagAssignments` and `wallGroups` both key on a bare `face_index`, and
  // two parts do not share an index space, so a carried-over label names an
  // unrelated face on the new part. That is the "I labelled one face and it
  // claimed two" report — the second label was already there, inherited.
  //
  // The clear lives on the store write for the same reason
  // `setInternalFlowMeshSource` does (see its comment in `toolSlice`):
  // `BoundaryAssignmentPanel` is mounted CONDITIONALLY by
  // `MeshAuthoringSection`, and its own guard captures the geometry id in a
  // ref at MOUNT. A switch made while the panel is closed is therefore
  // invisible to it — the ref re-initialises to the new id on reopen and the
  // stale labels survive, get rebound by index, and are baked in by
  // `materializeAll` as if the user had typed them.
  //
  // Guarded on an actual change so re-selecting the same geometry (a common
  // no-op from the tree) does not wipe work in progress.
  setActiveGeometryId: (v) =>
    set((s) =>
      s.activeGeometryId === v
        ? s
        : { activeGeometryId: v, ...versionScopedReset(s.activeGeometryId, v) },
    ),

  showGeometryPreview: (geometryId) =>
    set((s) => ({
      activeGeometryId: geometryId,
      selectedMeshId: null,
      selectedMeshCompleted: false,
      previewSelected: true,
      // A tessellation failure from another version must not drive this
      // version's preview fallback or unavailable message.
      cadPreviewError: null,
      ...versionScopedReset(s.activeGeometryId, geometryId),
    })),
  selectMeshInTree: (meshId) =>
    set({
      previewSelected: false,
      selectedMeshId: meshId,
      selectedMeshCompleted: true,
    }),

  setLogs: (v) =>
    set((s) => ({ logs: typeof v === 'function' ? v(s.logs) : v })),
  addLog: (v) => set((s) => ({ logs: [...s.logs, v] })),
  appendLogs: (v) => set((s) => ({ logs: [...s.logs, ...v] })),

  setCadPreviewGeometries: (v) => set({ cadPreviewGeometries: v }),
  setViewerCellCounts: (v) => set({ viewerCellCounts: v }),
  setViewerRegions: (v) => set({ viewerRegions: v }),
  setRegionVisibility: (v) => set({ regionVisibility: v }),
  toggleRegionVisibility: (tag, allKnownTags) =>
    set((s) => {
      const next = new Set(s.regionVisibility ?? allKnownTags);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return { regionVisibility: next };
    }),
  setIsClientTessellating: (v) => set({ isClientTessellating: v }),
  setCadPreviewError: (v) => set({ cadPreviewError: v }),
  setCadDiagonal: (v) => set({ cadDiagonal: v }),
  setCadBounds: (v) => set({ cadBounds: v }),
  setGeometryBounds: (v) => set({ geometryBounds: v }),
  setCadPreviewForGeometry: (geometryId, data) =>
    set((s) => {
      // Skip the spread when nothing would change — avoids re-rendering
      // every selector that subscribes to the cache (e.g. each open
      // GeometryRenderer) on a clearing call for an absent key.
      const present = geometryId in s.cadPreviewByGeometryId;
      if (!data && !present) return s;
      const next = { ...s.cadPreviewByGeometryId };
      if (data) next[geometryId] = data;
      else delete next[geometryId];
      return { cadPreviewByGeometryId: next };
    }),

  setDiagnosisSummary: (v) => set({ diagnosisSummary: v }),

  resetProjectState: () => set({ ...sceneInitialState }),
});
